import type { FacilityCreationFailedAction } from '@Application/Facilities/Facilities/Actions/facilityCreationFailed';
import type { FacilityUpdateFailedAction } from '@Application/Facilities/Facilities/Actions/facilityUpdateFailed';
import CreateFacility from '@Application/Facilities/Facilities/Commands/CreateFacility';
import UpdateFacility from '@Application/Facilities/Facilities/Commands/UpdateFacility';
import createFacility from '@Application/Facilities/Facilities/createFacility';
import updateFacility from '@Application/Facilities/Facilities/updateFacility';
import facilitySidebarSignal, {
	closeFacilitySidebar,
} from '@UI/Pages/Agencies/Facilities/Signals/facilitySidebarSignal';
import FacilityFormData from '@UI/Pages/Agencies/Facilities/Sidebars/FacilitySidebar/FormData/FacilityFormData';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar } from '@verclam/ui-kit';

/**
 * One panel for both creation and edition, told apart by `facilityId` on the signal's context.
 * Reads the edited facility straight from the store rather than through a prop: the page mounts this
 * component once, with no data of its own to pass it.
 */
const FacilitySidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(facilitySidebarSignal);
	const facilities = useSelector((state) => state.facilitiesStore.facilities);
	const { currentOrganization } = useCurrentOrganization();

	const [formData, setFormData] = React.useState(() => FacilityFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const facilityId = context?.facilityId ?? null;

	const editedFacility = React.useMemo(() => {
		if (facilityId === null || !facilities) {
			return null;
		}

		return facilities.findById(facilityId);
	}, [facilityId, facilities]);

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		setFormData(
			editedFacility !== null
				? FacilityFormData.fromView(editedFacility)
				: FacilityFormData.createEmpty(),
		);
	}, [context, editedFacility]);

	const handleNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setName(value));
	}, []);

	const handleAddressChange = React.useCallback((value: string) => {
		setFormData((current) => current.setAddress(value));
	}, []);

	const handlePhoneNumberChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPhoneNumber(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppMain` never mounts this page without a resolved organization.
		if (currentOrganization === null) {
			return;
		}

		const organizationId = currentOrganization.id;
		const address = formData.address === '' ? null : formData.address;
		const phoneNumber = formData.phoneNumber === '' ? null : formData.phoneNumber;

		setIsSubmitting(true);

		if (facilityId === null) {
			dispatch(createFacility(new CreateFacility(organizationId, formData.name, address, phoneNumber)))
				.then(() => closeFacilitySidebar())
				.catch((action: FacilityCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'FACILITY_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateFacility(
				new UpdateFacility(organizationId, facilityId, formData.name, address, phoneNumber),
			),
		)
			.then(() => closeFacilitySidebar())
			.catch((action: FacilityUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'FACILITY_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, facilityId, currentOrganization, dispatch]);

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeFacilitySidebar}
			position="right"
			header={facilityId === null ? 'Nouvelle agence' : 'Modifier l’agence'}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeFacilitySidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<Form
				id={formId}
				onSubmit={handleSubmit}
				aria-label={facilityId === null ? 'Créer une agence' : 'Modifier une agence'}
			>
				<InputText
					name="name"
					label="Nom"
					required
					value={formData.name}
					onChange={handleNameChange}
					errorMessage={formData.getValidationMessage('name')}
					isFormFieldValid={formData.isValid('name')}
				/>
				<InputText
					name="address"
					label="Adresse"
					value={formData.address ?? ''}
					onChange={handleAddressChange}
					errorMessage={formData.getValidationMessage('address')}
					isFormFieldValid={formData.isValid('address')}
				/>
				<InputText
					name="phoneNumber"
					label="Téléphone"
					type="tel"
					value={formData.phoneNumber ?? ''}
					onChange={handlePhoneNumberChange}
					errorMessage={formData.getValidationMessage('phoneNumber')}
					isFormFieldValid={formData.isValid('phoneNumber')}
				/>
			</Form>
		</Sidebar>
	);
};

export default FacilitySidebar;
